import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BrandMark from '../common/BrandMark';
import { brand, navigationItems } from '../../data/siteContent';
import { prefersReducedMotion } from '../../utils/motion';
import './Navbar.css';

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const progressRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    let lastY = window.scrollY;
    let frame = 0;
    const reduceMotion = prefersReducedMotion();

    const update = () => {
      frame = 0;
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`;
      }
      setScrolled(y > 20);
      // Hide while scrolling down past the hero, show again on scroll up.
      if (!reduceMotion) setHidden(y > lastY && y > 320);
      lastY = y;
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.body.classList.add('nav-menu-open');
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.classList.remove('nav-menu-open');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  return (
    <header
      className={`ct-navbar ${scrolled ? 'ct-navbar--scrolled' : ''} ${hidden && !menuOpen ? 'ct-navbar--hidden' : ''}`}
      id="main-navbar"
    >
      <div className="ct-progress" aria-hidden="true"><span ref={progressRef} /></div>
      <div className="ct-navbar-inner">
        <Link
          to="/"
          className="ct-logo"
          aria-label="CrediToolsMX - Inicio"
          onClick={() => {
            setMenuOpen(false);
            if (location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <BrandMark className="ct-logo-mark" size={32} />
          <span className="ct-logo-lockup">
            <span className="ct-logo-text">
              <span className="ct-logo-credit">CrediTools</span>
              <span className="ct-logo-mx">MX</span>
            </span>
            <span className="ct-logo-tagline">{brand.tagline}</span>
          </span>
        </Link>

        <button
          type="button"
          className={`ct-hamburger ${menuOpen ? 'ct-hamburger--open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Cerrar menú de navegación' : 'Abrir menú de navegación'}
          aria-expanded={menuOpen}
          aria-controls="main-navigation"
          id="mobile-menu-toggle"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        
        {menuOpen && (
          <div 
            className="ct-nav-backdrop" 
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <nav
          className={`ct-nav-links ${menuOpen ? 'ct-nav-links--open' : ''}`}
          id="main-navigation"
          aria-label="Navegación principal"
        >
          {navigationItems.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`ct-nav-link ${location.pathname === item.to ? 'ct-nav-link--active' : ''}`}
              onClick={() => {
                setMenuOpen(false);
                if (location.pathname === item.to) {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              {item.label}
            </Link>
          ))}
          <Link to="/contacto?intent=diagnostico" className="ct-nav-cta" onClick={() => setMenuOpen(false)}>
            Agenda 15 min
          </Link>
        </nav>
      </div>
    </header>
  );
}
